// The affiliate account's entire world: a minimal MARS-blue header and their
// message threads. Left pane has two tabs — "Chats" (own conversations) and
// "MARS Users" (all active MARS users with name + company, searchable) so
// affiliates can start conversations with brands too.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, MessageSquare, Loader2, Users, Search, Building2 } from 'lucide-react';
import ConversationThread from '../messaging/ConversationThread';
import ChatNotifications from '../messaging/ChatNotifications';
import { onChatEvent } from '../messaging/chatSocket';

const getApiUrl = () => process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const LIST_POLL_MS = 15000;

const AffiliatePortalLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [affiliate, setAffiliate] = useState(null);

  const [activeTab, setActiveTab] = useState('chats'); // 'chats' | 'users'
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  // ?open=<conversationId> deep link (used by notification popups)
  useEffect(() => {
    const open = new URLSearchParams(location.search).get('open');
    if (open) {
      setSelectedId(parseInt(open, 10));
      setActiveTab('chats');
    }
  }, [location.search]);

  const [directorySearch, setDirectorySearch] = useState('');
  const [marsUsers, setMarsUsers] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [startingId, setStartingId] = useState(null);

  // Identity comes from the affiliate session (separate from the main
  // users-table Redux auth).
  useEffect(() => {
    const loadIdentity = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/affiliate-auth/verify-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hasSession) {
            setAffiliate(data.affiliate);
          }
        }
      } catch (_) { /* header just shows no name */ }
    };
    loadIdentity();
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/affiliate-messaging/conversations`, {
        credentials: 'include',
        headers: { 'X-Mars-Actor': 'affiliate' }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (_) { /* retried on next poll */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchConversations();
    }, LIST_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Refresh the list instantly when any chat event arrives over the socket.
  useEffect(() => onChatEvent('affiliate', () => fetchConversations()), [fetchConversations]);

  const fetchMarsUsers = useCallback(async (search) => {
    setDirectoryLoading(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/affiliate-messaging/users?search=${encodeURIComponent(search || '')}`,
        { credentials: 'include', headers: { 'X-Mars-Actor': 'affiliate' } }
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) setMarsUsers(data.users);
    } catch (_) { /* directory shows empty state */ } finally {
      setDirectoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'users') return;
    const timer = setTimeout(() => fetchMarsUsers(directorySearch), 300);
    return () => clearTimeout(timer);
  }, [activeTab, directorySearch, fetchMarsUsers]);

  const startConversation = async (userId) => {
    if (startingId) return;
    setStartingId(userId);
    try {
      const response = await fetch(`${getApiUrl()}/api/affiliate-messaging/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Mars-Actor': 'affiliate' },
        credentials: 'include',
        body: JSON.stringify({ userId })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        await fetchConversations();
        setSelectedId(data.conversation.id);
        setActiveTab('chats');
      }
    } catch (_) { /* affiliate can click again */ } finally {
      setStartingId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${getApiUrl()}/api/affiliate-auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (_) { /* navigate away regardless */ }
    navigate('/login', { replace: true });
  };

  const selected = conversations.find(c => c.id === selectedId);
  const counterpartName = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.business_email;

  const tabButton = (key, label, Icon) => (
    <button
      onClick={() => setActiveTab(key)}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2 ${
        activeTab === key
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-slate-400'
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-slate-950">
      {/* New-message popups + desktop notifications */}
      <ChatNotifications role="affiliate" />
      {/* Minimal header — logo, identity, logout. Nothing else. */}
      <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/MARS-Color.png" alt="MARS" className="h-7 dark:hidden" />
          <img src="/MARS-White.png" alt="MARS" className="h-7 hidden dark:block" />
          <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-0.5">
            Affiliate Partner
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600 dark:text-slate-300">
            {affiliate ? `${affiliate.firstName || ''} ${affiliate.lastName || ''}`.trim() || affiliate.email : ''}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      {/* Two-pane messages */}
      <div className="flex-1 flex min-h-0">
        {/* Left pane: tabs + list */}
        <aside className="w-80 border-r border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
          <div className="flex border-b border-gray-200 dark:border-slate-700">
            {tabButton('chats', 'Chats', MessageSquare)}
            {tabButton('users', 'MARS Users', Users)}
          </div>

          {activeTab === 'chats' && (
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                </div>
              )}
              {!loading && conversations.length === 0 && (
                <div className="px-5 py-10 text-center">
                  <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No messages yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Open the MARS Users tab to contact a brand.</p>
                </div>
              )}
              {conversations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left px-5 py-3 border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors ${
                    selectedId === c.id ? 'bg-blue-50 dark:bg-slate-800' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {counterpartName(c)}
                    </p>
                    {c.unread_count > 0 && (
                      <span className="ml-2 flex-shrink-0 text-[10px] font-semibold bg-blue-600 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                  {c.company_name && (
                    <p className="text-xs text-gray-400 truncate flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {c.company_name}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                    {c.last_message_preview || 'New conversation'}
                  </p>
                </button>
              ))}
            </div>
          )}

          {activeTab === 'users' && (
            <>
              <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-800">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={directorySearch}
                    onChange={(e) => setDirectorySearch(e.target.value)}
                    placeholder="Search by name or company..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {directoryLoading && (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  </div>
                )}
                {!directoryLoading && marsUsers.length === 0 && (
                  <p className="text-center text-sm text-gray-500 py-8 px-5">No MARS users found.</p>
                )}
                {marsUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => startConversation(u.id)}
                    disabled={startingId === u.id}
                    className="w-full text-left px-4 py-3 border-b border-gray-100 dark:border-slate-800 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {`${u.first_name || ''} ${u.last_name || ''}`.trim() || 'MARS User'}
                      </p>
                      {startingId === u.id && <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />}
                    </div>
                    {u.company_name && (
                      <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                        <Building2 className="w-3 h-3" /> {u.company_name}
                      </p>
                    )}
                    {u.conversation_id && (
                      <p className="text-[10px] text-blue-600 mt-0.5">Existing conversation — opens the thread</p>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </aside>

        {/* Thread */}
        <main className="flex-1 min-w-0">
          {selected ? (
            <ConversationThread
              conversationId={selected.id}
              title={counterpartName(selected)}
              subtitle={selected.company_name || selected.business_email}
              onActivity={fetchConversations}
              actorRole="affiliate"
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Select a chat, or contact a brand from the MARS Users tab.</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AffiliatePortalLayout;
